/**
 * Abstract Base Class for Residential Properties
 * 
 * This provides common functionality for all residential property types
 * (Apartments, Houses, Villas, etc.)
 * 
 * Shared characteristics:
 * - Bedrooms and bathrooms
 * - Living spaces (living room, dining room, kitchen)
 * - Residential features (garden, pool, garage)
 * - Family-oriented amenities
 */

import { PropertyTypeHandler, PropertyCategory, PropertyValidationRule } from './property-type-handler.abstract';
import { RoomTypeConfig, FeatureConfig, RoomFormField, FeatureCategory as FeatureScopeCategory } from '@core/services/property-analysis/property-room-config.service';

export abstract class ResidentialPropertyHandler extends PropertyTypeHandler {
  readonly category = PropertyCategory.Residential;

  /**
   * All residential properties share these core room types
   */
  protected getCoreResidentialRoomTypes(): RoomTypeConfig[] {
    return [
      {
        typeKey: 'bedroom',
        label: 'properties.roomTypes.bedroom.label',
        pluralLabel: 'properties.roomTypes.bedroom.plural',
        countField: 'bedrooms',
        icon: 'bed',
        defaultCount: 0,
        description: 'properties.roomTypes.bedroom.description',
        formFields: this.getBedroomFormFields()
      },
      {
        typeKey: 'bathroom',
        label: 'properties.roomTypes.bathroom.label',
        pluralLabel: 'properties.roomTypes.bathroom.plural',
        countField: 'bathrooms',
        icon: 'bathroom',
        defaultCount: 0,
        description: 'properties.roomTypes.bathroom.description',
        formFields: this.getBathroomFormFields()
      },
      {
        typeKey: 'kitchen',
        label: 'properties.roomTypes.kitchen.label',
        pluralLabel: 'properties.roomTypes.kitchen.plural',
        countField: 'kitchens',
        icon: 'kitchen',
        defaultCount: 0,
        description: 'properties.roomTypes.kitchen.description',
        formFields: this.getKitchenFormFields()
      },
      {
        typeKey: 'living_room',
        label: 'properties.roomTypes.living_room.label',
        pluralLabel: 'properties.roomTypes.living_room.plural',
        countField: 'livingRooms',
        icon: 'weekend',
        defaultCount: 0,
        description: 'properties.roomTypes.living_room.description',
        formFields: this.getLivingRoomFormFields()
      },
      {
        typeKey: 'dining_room',
        label: 'properties.roomTypes.dining_room.label',
        pluralLabel: 'properties.roomTypes.dining_room.plural',
        countField: 'diningRooms',
        icon: 'dining',
        defaultCount: 0,
        description: 'properties.roomTypes.dining_room.description',
        formFields: this.getDiningRoomFormFields()
      },
      {
        typeKey: 'office',
        label: 'properties.roomTypes.home_office.label',
        pluralLabel: 'properties.roomTypes.home_office.plural',
        countField: 'officeSpaces',
        icon: 'work',
        defaultCount: 0,
        description: 'properties.roomTypes.home_office.description',
        formFields: this.getOfficeFormFields()
      },
      {
        typeKey: 'laundry',
        label: 'properties.roomTypes.laundry.label',
        pluralLabel: 'properties.roomTypes.laundry.plural',
        countField: 'laundryRooms',
        icon: 'local_laundry_service',
        defaultCount: 0,
        description: 'properties.roomTypes.laundry.description',
        formFields: this.getLaundryFormFields()
      },
      {
        typeKey: 'balcony',
        label: 'properties.roomTypes.balcony.label',
        pluralLabel: 'properties.roomTypes.balcony.plural',
        countField: 'balconies',
        icon: 'balcony',
        defaultCount: 0,
        description: 'properties.roomTypes.balcony.description',
        formFields: this.getBalconyFormFields()
      },
      {
        typeKey: 'terrace',
        label: 'properties.roomTypes.terrace.label',
        pluralLabel: 'properties.roomTypes.terrace.plural',
        countField: 'terraces',
        icon: 'deck',
        defaultCount: 0,
        description: 'properties.roomTypes.terrace.description',
        formFields: this.getTerraceFormFields()
      },
      {
        typeKey: 'custom',
        label: 'properties.roomTypes.custom.label',
        pluralLabel: 'properties.roomTypes.custom.plural',
        countField: 'customRooms', // Generic count field for custom rooms
        icon: 'add_box',
        defaultCount: 0,
        description: 'properties.roomTypes.custom.description',
        formFields: this.getCustomRoomFormFields()
      }
    ];
  }

  /**
   * Common residential features shared across apartments, houses, villas
   */
  protected getCoreResidentialFeatures(): FeatureConfig[] {
    return [
      {
        key: 'pool',
        label: 'properties.featureTypes.pool.label',
        icon: 'pool',
        category: FeatureScopeCategory.FLEXIBLE, // Can be private (property) or building amenity
        description: 'properties.featureTypes.pool.description',
        detailsInterface: 'PoolDetail',
        formFields: this.getPoolFormFields()
      },
      {
        key: 'garden',
        label: 'properties.featureTypes.garden.label',
        icon: 'nature',
        category: FeatureScopeCategory.PROPERTY_ONLY, // Private gardens are always property-specific
        description: 'properties.featureTypes.garden.description',
        detailsInterface: 'GardenDetail',
        formFields: this.getGardenFormFields()
      },
      {
        key: 'garage',
        label: 'properties.featureTypes.garage.label',
        icon: 'garage',
        category: FeatureScopeCategory.FLEXIBLE, // Can be private or building garage
        description: 'properties.featureTypes.garage.description',
        detailsInterface: 'GarageDetail',
        formFields: this.getGarageFormFields()
      },
      {
        key: 'security',
        label: 'properties.featureTypes.security.label',
        icon: 'security',
        category: FeatureScopeCategory.FLEXIBLE, // Can be unit alarm OR building security
        description: 'properties.featureTypes.security.description',
        detailsInterface: 'SecurityDetail',
        formFields: this.getSecurityFormFields()
      },
      {
        key: 'laundry',
        label: 'properties.featureTypes.laundry.label',
        icon: 'local_laundry_service',
        category: FeatureScopeCategory.FLEXIBLE, // Can be in-unit or shared building laundry
        description: 'properties.featureTypes.laundry.description',
        detailsInterface: 'LaundryDetail',
        formFields: this.getLaundryFormFields()
      }
    ];
  }

  /**
   * Common main characteristics for residential properties
   */
  getMainCharacteristics(): string[] {
    return ['bedrooms', 'bathrooms', 'surface', 'balconies', 'parkingSpaces'];
  }

  /**
   * Common validation rules for residential properties
   */
  getValidationRules(): PropertyValidationRule[] {
    return [
      {
        fieldName: 'characteristics.bedrooms',
        rules: {
          min: 0,
          max: 20,
          errorMessage: 'Bedrooms must be between 0 and 20'
        }
      },
      {
        fieldName: 'characteristics.bathrooms',
        rules: {
          min: 0,
          max: 15,
          errorMessage: 'Bathrooms must be between 0 and 15'
        }
      },
      {
        fieldName: 'surfaces.netSurface',
        rules: {
          required: true,
          min: 10,
          errorMessage: 'Surface area must be at least 10 m²'
        }
      }
    ];
  }

  // ==================== FORM FIELD DEFINITIONS ====================

  /**
   * Get common dimension fields for all room types
   * Includes: name, length, width, sqm (with auto-calculation logic)
   *
   * Note: The actual auto-calculation happens in the dialog component,
   * but these fields define the structure
   */
  protected getCommonDimensionFields(_roomTypeName: string): RoomFormField[] {
    return [
      {
        name: 'name',
        label: 'properties.fields.name',
        type: 'text',
        placeholder: 'properties.fieldPlaceholders.roomName',
        hint: 'properties.fieldHints.roomName'
      },
      {
        name: 'length',
        label: 'properties.fields.length',
        type: 'number',
        suffix: 'm',
        min: 0.1,
        max: 100,
        step: 0.1,
        placeholder: '0.0',
        hint: 'properties.fieldHints.length'
      },
      {
        name: 'width',
        label: 'properties.fields.width',
        type: 'number',
        suffix: 'm',
        min: 0.1,
        max: 100,
        step: 0.1,
        placeholder: '0.0',
        hint: 'properties.fieldHints.width'
      },
      {
        name: 'sqm',
        label: 'properties.fields.surfaceArea',
        type: 'number',
        suffix: 'm²',
        min: 0.1,
        max: 500,
        step: 0.1,
        placeholder: '0.0',
        hint: 'properties.fieldHints.sqm'
      }
    ];
  }

  protected getBedroomFormFields(): RoomFormField[] {
    return [
      // Common fields: name, length, width, sqm
      ...this.getCommonDimensionFields('Bedroom'),
      {
        name: 'hasBalcony',
        label: 'properties.fields.hasBalcony',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasEnsuite',
        label: 'properties.fields.hasEnsuite',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasDressing',
        label: 'properties.fields.hasDressing',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasBuiltInWardrobe',
        label: 'properties.fields.hasBuiltInWardrobe',
        type: 'checkbox',
        default: false
      },
      {
        name: 'windowOrientation',
        label: 'properties.fields.windowOrientation',
        type: 'select',
        options: [
          { value: 'north', label: 'properties.fieldOptions.orientation.north' },
          { value: 'south', label: 'properties.fieldOptions.orientation.south' },
          { value: 'east', label: 'properties.fieldOptions.orientation.east' },
          { value: 'west', label: 'properties.fieldOptions.orientation.west' },
          { value: 'northeast', label: 'properties.fieldOptions.orientation.northeast' },
          { value: 'northwest', label: 'properties.fieldOptions.orientation.northwest' },
          { value: 'southeast', label: 'properties.fieldOptions.orientation.southeast' },
          { value: 'southwest', label: 'properties.fieldOptions.orientation.southwest' }
        ]
      },
      {
        name: 'floorType',
        label: 'properties.fields.floorType',
        type: 'select',
        options: [
          { value: 'hardwood', label: 'properties.fieldOptions.floorType.hardwood' },
          { value: 'laminate', label: 'properties.fieldOptions.floorType.laminate' },
          { value: 'carpet', label: 'properties.fieldOptions.floorType.carpet' },
          { value: 'tile', label: 'properties.fieldOptions.floorType.tile' },
          { value: 'parquet', label: 'properties.fieldOptions.floorType.parquet' }
        ]
      },
      {
        name: 'notes',
        label: 'properties.fields.additionalNotes',
        type: 'textarea',
        placeholder: 'properties.fieldPlaceholders.notesBedroom'
      }
    ];
  }

  protected getBathroomFormFields(): RoomFormField[] {
    return [
      // Common fields: name, length, width, sqm
      ...this.getCommonDimensionFields('Bathroom'),
      {
        name: 'hasToilet',
        label: 'properties.fields.hasToilet',
        type: 'checkbox',
        default: true
      },
      {
        name: 'hasShower',
        label: 'properties.fields.hasShower',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasBathtub',
        label: 'properties.fields.hasBathtub',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasDoubleSink',
        label: 'properties.fields.hasDoubleSink',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasWindow',
        label: 'properties.fields.hasWindow',
        type: 'checkbox',
        default: false
      },
      {
        name: 'floorHeating',
        label: 'properties.fields.floorHeating',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasBidet',
        label: 'properties.fields.hasBidet',
        type: 'checkbox',
        default: false
      },
      {
        name: 'ventilationType',
        label: 'properties.fields.ventilationType',
        type: 'select',
        options: [
          { value: 'natural', label: 'properties.fieldOptions.ventilationType.natural' },
          { value: 'mechanical', label: 'properties.fieldOptions.ventilationType.mechanical' },
          { value: 'none', label: 'properties.fieldOptions.ventilationType.none' }
        ]
      },
      {
        name: 'notes',
        label: 'properties.fields.additionalNotes',
        type: 'textarea'
      }
    ];
  }

  protected getKitchenFormFields(): RoomFormField[] {
    return [
      // Common fields: name, length, width, sqm
      ...this.getCommonDimensionFields('Kitchen'),
      {
        name: 'kitchenType',
        label: 'properties.fields.kitchenType',
        type: 'select',
        options: [
          { value: '', label: 'properties.fieldOptions.common.notIndicated' },
          { value: 'open', label: 'properties.fieldOptions.kitchenType.open' },
          { value: 'semi_open', label: 'properties.fieldOptions.kitchenType.semi_open' },
          { value: 'closed', label: 'properties.fieldOptions.kitchenType.closed' }
        ]
      },
      {
        name: 'appliances',
        label: 'properties.fields.appliancesIncluded',
        type: 'checkbox',
        default: false
      },
      {
        name: 'countertopMaterial',
        label: 'properties.fields.countertopMaterial',
        type: 'select',
        options: [
          { value: '', label: 'properties.fieldOptions.common.notIndicated' },
          { value: 'granite', label: 'properties.fieldOptions.countertopMaterial.granite' },
          { value: 'marble', label: 'properties.fieldOptions.countertopMaterial.marble' },
          { value: 'quartz', label: 'properties.fieldOptions.countertopMaterial.quartz' },
          { value: 'laminate', label: 'properties.fieldOptions.countertopMaterial.laminate' },
          { value: 'wood', label: 'properties.fieldOptions.countertopMaterial.wood' }
        ]
      },
      {
        name: 'notes',
        label: 'properties.fields.additionalNotes',
        type: 'textarea',
        placeholder: 'properties.fieldPlaceholders.notesKitchen'
      }
    ];
  }

  protected getLivingRoomFormFields(): RoomFormField[] {
    return [
      // Common fields: name, length, width, sqm
      ...this.getCommonDimensionFields('Living Room'),
      {
        name: 'floorType',
        label: 'properties.fields.flooringType',
        type: 'select',
        options: [
          { value: '', label: 'properties.fieldOptions.common.notIndicated' },
          { value: 'hardwood', label: 'properties.fieldOptions.floorType.hardwood' },
          { value: 'carpet', label: 'properties.fieldOptions.floorType.carpet' },
          { value: 'tile', label: 'properties.fieldOptions.floorType.tile' },
          { value: 'laminate', label: 'properties.fieldOptions.floorType.laminate' },
          { value: 'marble', label: 'properties.fieldOptions.floorType.marble' }
        ]
      },
      {
        name: 'hasFireplace',
        label: 'properties.fields.fireplace',
        type: 'checkbox',
        default: false
      },
      {
        name: 'numberOfWindows',
        label: 'properties.fields.numberOfWindows',
        type: 'number',
        min: 0,
        max: 10
      },
      {
        name: 'windowOrientation',
        label: 'properties.fields.windowOrientation',
        type: 'select',
        options: [
          { value: '', label: 'properties.fieldOptions.common.notIndicated' },
          { value: 'north', label: 'properties.fieldOptions.orientation.north' },
          { value: 'south', label: 'properties.fieldOptions.orientation.south' },
          { value: 'east', label: 'properties.fieldOptions.orientation.east' },
          { value: 'west', label: 'properties.fieldOptions.orientation.west' }
        ]
      },
      {
        name: 'hasAirConditioning',
        label: 'properties.fields.airConditioning',
        type: 'checkbox',
        default: false
      },
      {
        name: 'notes',
        label: 'properties.fields.additionalNotes',
        type: 'textarea',
        placeholder: 'properties.fieldPlaceholders.notesLivingRoom'
      }
    ];
  }

  protected getDiningRoomFormFields(): RoomFormField[] {
    return [
      // Common fields: name, length, width, sqm
      ...this.getCommonDimensionFields('Dining Room'),
      {
        name: 'seatingCapacity',
        label: 'properties.fields.seatingCapacity',
        type: 'number',
        min: 2,
        max: 20,
        placeholder: 'properties.fieldPlaceholders.numberOfPeople'
      },
      {
        name: 'hasBuiltInCabinets',
        label: 'properties.fields.builtInCabinets',
        type: 'checkbox',
        default: false
      },
      {
        name: 'notes',
        label: 'properties.fields.additionalNotes',
        type: 'textarea',
        placeholder: 'properties.fieldPlaceholders.notesDiningRoom'
      }
    ];
  }

  protected getOfficeFormFields(): RoomFormField[] {
    return [
      // Common fields: name, length, width, sqm
      ...this.getCommonDimensionFields('Home Office'),
      {
        name: 'hasBuiltInDesk',
        label: 'properties.fields.builtInDesk',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasStorage',
        label: 'properties.fields.storageShelving',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasWindow',
        label: 'properties.fields.hasWindow',
        type: 'checkbox',
        default: false
      },
      {
        name: 'notes',
        label: 'properties.fields.additionalNotes',
        type: 'textarea',
        placeholder: 'properties.fieldPlaceholders.notesOffice'
      }
    ];
  }

  protected getBalconyFormFields(): RoomFormField[] {
    return [
      // Common fields: name, length, width, sqm
      ...this.getCommonDimensionFields('Balcony'),
      {
        name: 'orientation',
        label: 'properties.fields.orientation',
        type: 'select',
        options: [
          { value: '', label: 'properties.fieldOptions.common.notIndicated' },
          { value: 'north', label: 'properties.fieldOptions.orientation.north' },
          { value: 'south', label: 'properties.fieldOptions.orientation.south' },
          { value: 'east', label: 'properties.fieldOptions.orientation.east' },
          { value: 'west', label: 'properties.fieldOptions.orientation.west' },
          { value: 'north_east', label: 'properties.fieldOptions.orientation.northeast' },
          { value: 'north_west', label: 'properties.fieldOptions.orientation.northwest' },
          { value: 'south_east', label: 'properties.fieldOptions.orientation.southeast' },
          { value: 'south_west', label: 'properties.fieldOptions.orientation.southwest' }
        ]
      },
      {
        name: 'isCovered',
        label: 'properties.fields.covered',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasElectricity',
        label: 'properties.fields.hasElectricity',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasWaterConnection',
        label: 'properties.fields.waterConnection',
        type: 'checkbox',
        default: false
      },
      {
        name: 'notes',
        label: 'properties.fields.additionalNotes',
        type: 'textarea',
        placeholder: 'properties.fieldPlaceholders.notesBalcony'
      }
    ];
  }

  protected getTerraceFormFields(): RoomFormField[] {
    return [
      // Common fields: name, length, width, sqm
      ...this.getCommonDimensionFields('Terrace'),
      {
        name: 'type',
        label: 'properties.fields.terraceType',
        type: 'select',
        options: [
          { value: '', label: 'properties.fieldOptions.common.notIndicated' },
          { value: 'rooftop', label: 'properties.fieldOptions.terraceType.rooftop' },
          { value: 'ground_level', label: 'properties.fieldOptions.terraceType.ground_level' },
          { value: 'raised', label: 'properties.fieldOptions.terraceType.raised' }
        ]
      },
      {
        name: 'orientation',
        label: 'properties.fields.orientation',
        type: 'select',
        options: [
          { value: '', label: 'properties.fieldOptions.common.notIndicated' },
          { value: 'north', label: 'properties.fieldOptions.orientation.north' },
          { value: 'south', label: 'properties.fieldOptions.orientation.south' },
          { value: 'east', label: 'properties.fieldOptions.orientation.east' },
          { value: 'west', label: 'properties.fieldOptions.orientation.west' }
        ]
      },
      {
        name: 'isCovered',
        label: 'properties.fields.coveredProtected',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasElectricity',
        label: 'properties.fields.hasElectricity',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasWaterConnection',
        label: 'properties.fields.waterConnection',
        type: 'checkbox',
        default: false
      },
      {
        name: 'notes',
        label: 'properties.fields.additionalNotes',
        type: 'textarea',
        placeholder: 'properties.fieldPlaceholders.notesTerrace'
      }
    ];
  }

  protected getCustomRoomFormFields(): RoomFormField[] {
    return [
      {
        name: 'customRoomType',
        label: 'properties.fields.roomType',
        type: 'text',
        required: true,
        placeholder: 'properties.fieldPlaceholders.customRoomType',
        hint: 'properties.fieldHints.customRoomType'
      },
      // Common fields: name, length, width, sqm
      ...this.getCommonDimensionFields('Custom Room'),
      {
        name: 'hasWindow',
        label: 'properties.fields.hasWindow',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasElectricity',
        label: 'properties.fields.hasElectricity',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasWaterConnection',
        label: 'properties.fields.waterConnection',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasHeating',
        label: 'properties.fields.hasHeating',
        type: 'checkbox',
        default: false
      },
      {
        name: 'notes',
        label: 'properties.fields.additionalNotes',
        type: 'textarea',
        placeholder: 'properties.fieldPlaceholders.notesRoom'
      }
    ];
  }

  protected getPoolFormFields(): RoomFormField[] {
    return [
      {
        name: 'location',
        label: 'properties.fields.location',
        type: 'select',
        required: true,
        options: [
          { value: 'outdoor', label: 'properties.fieldOptions.poolLocation.outdoor' },
          { value: 'indoor', label: 'properties.fieldOptions.poolLocation.indoor' }
        ]
      },
      {
        name: 'length',
        label: 'properties.fields.length',
        type: 'number',
        suffix: 'm',
        min: 1,
        max: 50
      },
      {
        name: 'width',
        label: 'properties.fields.width',
        type: 'number',
        suffix: 'm',
        min: 1,
        max: 25
      },
      {
        name: 'depthMin',
        label: 'properties.fields.minimumDepth',
        type: 'number',
        suffix: 'm',
        min: 0.5,
        max: 5,
        step: 0.1
      },
      {
        name: 'depthMax',
        label: 'properties.fields.maximumDepth',
        type: 'number',
        suffix: 'm',
        min: 0.5,
        max: 5,
        step: 0.1
      },
      {
        name: 'hasHeating',
        label: 'properties.fields.poolHeating',
        type: 'checkbox',
        default: false
      },
      {
        name: 'heatingType',
        label: 'properties.fields.heatingType',
        type: 'select',
        options: [
          { value: 'heat_pump', label: 'properties.fieldOptions.poolHeatingType.heat_pump' },
          { value: 'solar', label: 'properties.fieldOptions.poolHeatingType.solar' },
          { value: 'gas', label: 'properties.fieldOptions.poolHeatingType.gas' },
          { value: 'electric', label: 'properties.fieldOptions.poolHeatingType.electric' }
        ]
      },
      {
        name: 'hasCover',
        label: 'properties.fields.poolCover',
        type: 'checkbox',
        default: false
      },
      {
        name: 'cleaningSystem',
        label: 'properties.fields.cleaningSystem',
        type: 'select',
        options: [
          { value: 'manual', label: 'properties.fieldOptions.cleaningSystem.manual' },
          { value: 'robotic', label: 'properties.fieldOptions.cleaningSystem.robotic' },
          { value: 'automatic', label: 'properties.fieldOptions.cleaningSystem.automatic' }
        ]
      }
    ];
  }

  protected getGardenFormFields(): RoomFormField[] {
    return [
      {
        name: 'totalSqm',
        label: 'properties.fields.totalArea',
        type: 'number',
        suffix: 'm²',
        required: true,
        min: 1
      },
      {
        name: 'isLandscaped',
        label: 'properties.fields.landscaped',
        type: 'checkbox',
        default: false
      },
      {
        name: 'lawnSqm',
        label: 'properties.fields.lawnArea',
        type: 'number',
        suffix: 'm²',
        min: 0
      },
      {
        name: 'pavedSqm',
        label: 'properties.fields.pavedArea',
        type: 'number',
        suffix: 'm²',
        min: 0
      },
      {
        name: 'hasIrrigation',
        label: 'properties.fields.irrigationSystem',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasLighting',
        label: 'properties.fields.gardenLighting',
        type: 'checkbox',
        default: false
      },
      {
        name: 'fenceType',
        label: 'properties.fields.fenceType',
        type: 'select',
        options: [
          { value: 'wood', label: 'properties.fieldOptions.fenceType.wood' },
          { value: 'metal', label: 'properties.fieldOptions.fenceType.metal' },
          { value: 'hedge', label: 'properties.fieldOptions.fenceType.hedge' },
          { value: 'brick', label: 'properties.fieldOptions.fenceType.brick' },
          { value: 'none', label: 'properties.fieldOptions.fenceType.none' }
        ]
      },
      {
        name: 'hasShed',
        label: 'properties.fields.gardenShed',
        type: 'checkbox',
        default: false
      }
    ];
  }

  protected getGarageFormFields(): RoomFormField[] {
    return [
      {
        name: 'garageType',
        label: 'properties.fields.garageType',
        type: 'select',
        required: true,
        options: [
          { value: 'attached', label: 'properties.fieldOptions.garageType.attached' },
          { value: 'detached', label: 'properties.fieldOptions.garageType.detached' },
          { value: 'underground', label: 'properties.fieldOptions.garageType.underground' },
          { value: 'carport', label: 'properties.fieldOptions.garageType.carport' }
        ]
      },
      {
        name: 'numberOfSpaces',
        label: 'properties.fields.numberOfSpaces',
        type: 'number',
        required: true,
        min: 1,
        max: 10
      },
      {
        name: 'sqm',
        label: 'properties.fields.area',
        type: 'number',
        suffix: 'm²',
        min: 10
      },
      {
        name: 'hasAutomaticDoor',
        label: 'properties.fields.automaticDoor',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasElectricCarCharger',
        label: 'properties.fields.electricCarCharger',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasDirectHouseAccess',
        label: 'properties.fields.directHouseAccess',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasElectricity',
        label: 'properties.fields.electricity',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasHeating',
        label: 'properties.fields.heating',
        type: 'checkbox',
        default: false
      }
    ];
  }

  protected getSecurityFormFields(): RoomFormField[] {
    return [
      {
        name: 'hasAlarm',
        label: 'properties.fields.alarmSystem',
        type: 'checkbox',
        default: false
      },
      {
        name: 'alarmBrand',
        label: 'properties.fields.alarmBrand',
        type: 'text'
      },
      {
        name: 'hasCCTV',
        label: 'properties.fields.cctvSystem',
        type: 'checkbox',
        default: false
      },
      {
        name: 'cameraCount',
        label: 'properties.fields.numberOfCameras',
        type: 'number',
        min: 1
      },
      {
        name: 'hasVideoIntercom',
        label: 'properties.fields.videoIntercom',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasSmartLocks',
        label: 'properties.fields.smartLocks',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasAccessControl',
        label: 'properties.fields.accessControl',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasMotionSensors',
        label: 'properties.fields.motionSensors',
        type: 'checkbox',
        default: false
      }
    ];
  }

  protected getLaundryFormFields(): RoomFormField[] {
    return [
      // Common fields: name, length, width, sqm
      ...this.getCommonDimensionFields('Laundry Room'),
      {
        name: 'location',
        label: 'properties.fields.location',
        type: 'select',
        required: true,
        options: [
          { value: 'dedicated_room', label: 'properties.fieldOptions.laundryLocation.dedicated_room' },
          { value: 'bathroom', label: 'properties.fieldOptions.laundryLocation.bathroom' },
          { value: 'kitchen', label: 'properties.fieldOptions.laundryLocation.kitchen' },
          { value: 'closet', label: 'properties.fieldOptions.laundryLocation.closet' },
          { value: 'basement', label: 'properties.fieldOptions.laundryLocation.basement' }
        ]
      },
      {
        name: 'hasWashingMachine',
        label: 'properties.fields.hasWashingMachine',
        type: 'checkbox',
        default: false
      },
      {
        name: 'washingMachineIncluded',
        label: 'properties.fields.washingMachineIncluded',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasDryer',
        label: 'properties.fields.hasDryer',
        type: 'checkbox',
        default: false
      },
      {
        name: 'dryerIncluded',
        label: 'properties.fields.dryerIncluded',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasSink',
        label: 'properties.fields.sink',
        type: 'checkbox',
        default: false
      },
      {
        name: 'hasVentilation',
        label: 'properties.fields.ventilation',
        type: 'checkbox',
        default: false
      }
    ];
  }
}

